/**
 * Personal boards slice (GOTY + interests ordering) — prototype `addPot`
 * (1780), `placeTier` (1705), `placeTierAt` (1369), `unplaceTier` (1725),
 * `skipTodoId` (1719), `nudgeTier` (1827), `steamFill` (1787).
 *
 * The four parallel structures are kept in the prototype's shape
 * (`{userKey: …}`) per ARCHITECTURE §0 D3, even though RLS means only the
 * signed-in member's key is ever populated — that is what lets the ported
 * derivations in `lib/logic.ts` run unmodified.
 *
 * `crewIntOrder` is the ONE map here that holds other people's rows, and it is
 * deliberately a sixth map rather than extra keys in `intOrder`: see its own
 * doc-comment. The sentence above still describes the five it is beside.
 */

import type { StateCreator } from 'zustand';

import type { GotyCandidate, SteamBackfill } from '../../repo/Repository.ts';

import {
  GOTY_YEAR,
  skipsAfterSkip,
  TIER_NAMES,
  tierOrderAfterDrop,
  tierOrderAfterNudge,
  tierOrderAfterPlace,
  tierRows,
} from '../../lib/logic.ts';
import type { Game, GameId, OrderMap, Tier, TierMap } from '../../lib/types.ts';
import { mutate, storeApi } from '../mutate.ts';
import type { QdStore } from '../types.ts';

/** What both paths into the GOTY board need to know about a game. */
type GotyPick = { appid: number; name: string };

type BoardsSet = Parameters<StateCreator<QdStore, [], [], BoardsSlice>>[0];
type BoardsGet = Parameters<StateCreator<QdStore, [], [], BoardsSlice>>[1];

/**
 * Crewmates whose ordering is being read right now — the double-ask guard for
 * `ensureCrewIntOrder`.
 *
 * A module singleton for the same bargain `mutate`'s `inFlight` map makes: in
 * production there is one store, and in a test file there is one per
 * `beforeEach`. Keys are removed in a `finally`, so a store torn down mid-read
 * leaves at most one stale key that the next `ensure` clears on its own answer.
 */
const crewOrderInFlight = new Set<string>();

/**
 * How many `ensure_game` mints may be in flight at once.
 *
 * G-06 justified an UNBOUNDED `Promise.allSettled` with "no concurrency cap is
 * needed because `op=goty` caps the list at 20 server-side". The picker (G-07)
 * removes that justification: a member can confirm anything they own, and a
 * 400-wide fan-out at one round trip each is a burst nobody asked for. The
 * mints stay independent and unordered — only how many run at once changes.
 */
export const MINT_CONCURRENCY = 8;

/** `Promise.allSettled` semantics with a bounded pool: one rejection costs only
 *  itself, and the result is positional so the caller can still line failures up
 *  against the input. */
export async function settlePooled<T>(
  items: readonly T[],
  run: (item: T) => Promise<unknown>,
  width: number,
): Promise<boolean[]> {
  const okFlags = new Array<boolean>(items.length).fill(false);
  let next = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const i = next++;
      const item = items[i];
      if (item === undefined) return;
      try {
        await run(item);
        okFlags[i] = true;
      } catch {
        okFlags[i] = false;
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(width, items.length) }, worker),
  );
  return okFlags;
}

/**
 * The GOTY auto-fill gate toast (INVENTORY-DESKTOP §8, D3-06).
 *
 * The desktop template names WHERE to go and mobile does not, so the one string
 * differs per tree. This slice is shared by both trees, and it must stay that
 * way — importing `desktop/useBreakpoint.ts` here would make the store depend on
 * the desktop tree (D0 §6 item 3 forbids crossing in either direction). So the
 * branch is a bare `matchMedia` read at EMIT time rather than at module load:
 * `steamFill` only ever runs from a user gesture, by which point the window is
 * whatever size it is, and reading late means a resize across 760px cannot leave
 * a stale string cached.
 *
 * The query matches `desktop/useBreakpoint.ts#DESKTOP_QUERY`. No `window` (SSR)
 * or no `matchMedia` (jsdom without a stub) falls back to the mobile string,
 * which is the same mobile-first default that hook's server snapshot takes.
 */
function steamFillGateToast(): string {
  const wide =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(min-width:760px)').matches;
  return wide
    ? 'LINK STEAM + SET IT PUBLIC (FRIENDS PAGE) TO AUTO-FILL'
    : 'LINK STEAM + SET IT PUBLIC TO AUTO-FILL';
}

/**
 * What went wrong upstream, in the copy the rest of the app already uses for
 * that state. Every branch is a REAL failure the member can act on — the point
 * of the G-01 rework is that none of them get to masquerade as "nothing new".
 */
function steamFillErrorToast(code: unknown): string {
  switch (code) {
    case 'NO_KEY':
      // The same state the sync sheet calls AUTO-DETECTION UNAVAILABLE.
      return 'AUTO-FILL UNAVAILABLE — ADD YOUR POTENTIALS BELOW';
    case 'NOT_LINKED':
      return steamFillGateToast();
    case 'THROTTLED':
      return 'CHECKED JUST NOW — TRY AGAIN IN A MOMENT';
    default:
      return "✕ STEAM DIDN'T ANSWER — TRY AGAIN";
  }
}

/**
 * A `Game` for an appid the crew catalog has never seen.
 *
 * Only `name` is real; everything else is the same "unknown until enriched"
 * shape `ensure_game` writes server-side, and `revalidate` fills in the price,
 * review score and art on the next pass. Deliberately NOT invented: a release
 * date, a player count or a tag list would be editorial fields sourced from
 * nothing (ARCHITECTURE §1.4 / decision D2).
 */
export function provisionalGame(g: { appid: number; name: string }): Game {
  return {
    id: g.appid,
    appid: g.appid,
    n: g.name || 'Steam App #' + g.appid,
    dev: '—',
    rd: '',
    p: null,
    o: 0,
    mn: 1,
    mx: 1,
    plat: 'P',
    tags: [],
    rp: 0,
    pk: 0,
    // Same reasoning as the fields above: a player count sourced from nothing
    // would be an invented editorial value. `popOf` falls back to `pk`.
    ccu: 0,
    up: false,
    ds: '',
    ea: false,
    demo: false,
    dAid: 0,
    art: '',
    live: false,
    thumb: '',
    prov: true,
  };
}

/**
 * Mint whatever the crew catalog is missing, then append to the member's board.
 *
 * Shared by the two ways Steam reaches the GOTY board: `steamFill` when Steam
 * dated the library, and `gotyPickConfirm` when it did not (G-07). They differ
 * only in where the list came from and what the toast says — every failure mode
 * below is identical, and having two copies of it was how one of them would
 * eventually lose the ordering rule.
 *
 *   'none'   nothing here that is not already on the board
 *   'failed' every mint was rejected, so there is nothing safe to add
 *   'ok'     written (optimistically — `mutate` toasts before the commit lands)
 */
async function commitGotyPicks(
  get: BoardsGet,
  set: BoardsSet,
  meKey: string,
  picks: readonly GotyPick[],
  toastOf: (n: number) => string,
): Promise<'none' | 'failed' | 'ok'> {
  const s = get();
  const cur = s.myPots();
  const have = new Set(cur.map(Number));
  const fresh = picks.filter((g) => !have.has(g.appid));
  if (!fresh.length) return 'none';

  // `ensure_game` is `on conflict do nothing`, so these are independent and need
  // no ordering. `add_goty_potential` inside `saveBoards` deliberately does NOT
  // follow suit: it derives `max(pot_pos) + 1` per call, so firing those
  // concurrently would race on the board's order.
  const needsMint = new Set(fresh.filter((g) => !s.gByAppid(g.appid)).map((g) => g.appid));
  const toMint = fresh.filter((g) => needsMint.has(g.appid));
  const minted$ = await settlePooled(
    toMint,
    (g) =>
      s.repo.ensureGame({
        appid: g.appid,
        name: g.name,
        dev: '',
        releaseYm: '',
        price: null,
        oldPrice: 0,
        upcoming: false,
        description: '',
        artUrl: '',
      }),
    MINT_CONCURRENCY,
  );
  const failed = new Set<number>();
  toMint.forEach((g, i) => {
    if (!minted$[i]) failed.add(g.appid);
  });

  // Rebuilt by walking `fresh`, NOT the settled array: the list arrives ranked
  // (most-played first from Steam, confident-first from the picker) and
  // `gotyQueue` orders the TO DO queue by insertion, so that ranking is what the
  // member works through. Assembling from completion order would shuffle it by
  // network timing. A game we could not mint is left out of `potentials`
  // entirely — including it would make `saveBoards` throw UNKNOWN_GAME and roll
  // the whole batch back.
  const minted: Game[] = [];
  const add: number[] = [];
  for (const g of fresh) {
    if (failed.has(g.appid)) continue;
    if (needsMint.has(g.appid)) minted.push(provisionalGame(g));
    add.push(g.appid);
  }
  if (!add.length) return 'failed';

  // Before the optimistic write, so `gotyVM` can resolve every id it is about to
  // see. `catalog` is a read-through cache, not mutation state, which is why it
  // is not in `mutate`'s rollback snapshot — a row for a game the commit then
  // rejects is harmless and the next `fetchCatalog` drops it.
  if (minted.length) set((st) => ({ catalog: [...st.catalog, ...minted] }));

  const potentials = [...cur, ...add];
  await mutate(storeApi(get, set), {
    key: 'boards:' + meKey,
    optimistic: (st) => ({ potentials: { ...st.potentials, [meKey]: potentials } }),
    commit: () => s.repo.saveBoards({ potentials }),
    toast: toastOf(add.length),
  });
  // Cover art for the new tiles; the minted rows have none of their own.
  s.queueAssets(add);
  return 'ok';
}

/**
 * The CHECK NOW that AUTO-FILL now runs for itself, deliberately silent.
 *
 * It is not just a saved tap. `syncOne` computes each ledger row as the growth
 * against the PREVIOUSLY STORED playtime, so syncing immediately before the
 * auto-fill is what turns "played something since last night's sweep" into a
 * dated event that `op=goty` can then find. Ordering is the whole point: run it
 * after, and the observation lands too late to be offered.
 *
 * BEST EFFORT, and never fatal. `steamSyncNow` answers `ok:false` with a code
 * rather than throwing — THROTTLED being the ordinary one, since a member who
 * pressed CHECK NOW half a minute ago is inside the server's 30 s window — and
 * none of that should stop an auto-fill that can read Steam live regardless.
 *
 * Silent because the member asked for an auto-fill, not for a sync report: the
 * store's own `steamSyncNow` action toasts on both outcomes, which is right for
 * a button that IS the check and pure noise in front of this one. The freshness
 * chip is still advanced on success, because a real sync really did happen. On
 * failure it is left alone rather than flipped to RETRYING — the auto-fill has
 * its own error to report, and one round trip failing is not evidence the link
 * is unhealthy.
 */
/**
 * A past year's release-date guesses: the crew catalog's games that came out
 * that year, biggest first, every one `released` and NOT confident — so the
 * picker labels them `CAME OUT THIS YEAR — WE ARE GUESSING` and leaves them
 * unticked. `skip` is what is already on the board or already offered.
 */
function releaseGuesses(
  get: BoardsGet,
  year: number,
  skip: ReadonlySet<number>,
): GotyCandidate[] {
  const prefix = String(year) + '-';
  return get()
    .catalog.filter((g) => !g.live && g.rd.startsWith(prefix) && !skip.has(Number(g.appid)))
    .sort((a, b) => b.pk - a.pk)
    .map((g) => ({
      appid: g.appid, name: g.n, playtimeMin: 0,
      signal: 'released', signals: ['released'], evidenceAt: 0, confident: false,
    }));
}

async function refreshBeforeFill(get: BoardsGet, set: BoardsSet): Promise<void> {
  const s = get();
  let res;
  try {
    res = await s.repo.steamSyncNow();
  } catch {
    return;
  }
  if (!res.ok) return;
  set((st) => ({
    steamLink: st.steamLink
      ? { ...st.steamLink, lastSyncAt: res.checkedAt, profilePublic: res.profilePublic }
      : st.steamLink,
  }));
  s.freshOk();
}

export interface BoardsSlice {
  /**
   * GAME OF THE YEAR, one list per year (AA phase 5; DESIGN-CHANGELOG
   * 2026-09-03). The store holds ONE year's board at a time — `gotyYear` —
   * and switching re-reads it; `gotyYears` is the row of years that exist,
   * from the table. `goty_entries.year` has carried this since the table was
   * made; the pinned `GOTY_YEAR` is now only the default and "this year".
   */
  gotyYear: number;
  gotyYears: number[];
  gotyYearMenu: boolean;
  setGotyYear: (y: number) => Promise<void>;
  toggleGotyYearMenu: (open?: boolean) => void;
  potentials: OrderMap;
  tiers: TierMap;
  tierOrder: OrderMap;
  skips: OrderMap;
  intOrder: OrderMap;
  /**
   * The MY INTERESTS skip queue — `skips`' twin one board over
   * (DESIGN-CHANGELOG 2026-08-24), and deliberately a SECOND map rather than a
   * shared one: the two boards ask different questions of it. A GOTY skip
   * defers a game you chose to rank; an interests skip defers a call on a game
   * the crew put in front of you, and the same appid can legitimately be
   * skipped on one board and featured on the other.
   *
   * Persisted as `interest_order.skip_pos` (3037), so SKIP FOR NOW survives a
   * reload — which is the whole difference between a queue and a shuffle.
   */
  intSkips: OrderMap;
  /**
   * OTHER members' MY INTERESTS orderings, keyed by user key — read on demand
   * from migration 3059's `crew_interest_order` and cached for the session.
   *
   * WHAT IT IS FOR. Games Tonight's WHO'S PLAYING rail filtered to ONE person
   * files every game under that person's call and says so (`MARCUS'S CALLS`).
   * The rows inside each tier were ordered by the group average, which with one
   * person selected is a constant — so the board came out in the order the crew
   * ADDED the games, not the order that member set. This map is what
   * `tonightVM` sorts by instead (Marcus, 2026-09-03).
   *
   * A SIXTH MAP RATHER THAN MORE KEYS IN `intOrder`, and the separation is
   * load-bearing. `intOrder` is MY board: `list.ts` writes it optimistically,
   * `mutate` rolls it back, `refreshBoards` refetches it, and every one of those
   * paths assumes the whole map is mine to overwrite. Folding a crewmate's
   * ordering in would put a read-only cache inside a write path and make a
   * failed drag capable of rolling back somebody else's row.
   *
   * PRESENCE IS THE CACHE, not truthiness: `[]` is the real answer for a member
   * who has ranked nothing, and a truthy guard would re-ask about them forever.
   * A FAILED read stores nothing at all, so the tier keeps its average sort and
   * the next selection retries — an empty array cached on error would be
   * indistinguishable from a member with an empty board.
   *
   * MY OWN key never lands here. `tonightVM` reads `intOrder[me]` for me, which
   * is the authoritative copy including an in-flight optimistic drag; asking the
   * server about myself would round-trip for a staler answer.
   */
  crewIntOrder: OrderMap;
  /**
   * `steamFill` is in flight — the P3e-03 busy flag, same shape as
   * `ptAddChecking`.
   *
   * It is doing real work now: one keyed Steam call plus a mint-and-add round
   * trip per game, which is seconds, not milliseconds. Without this the button
   * looked inert for the whole of it and the first sign anything had happened
   * was the toast at the end. It is also the double-tap guard — the button is a
   * plain `div`, so a second tap would otherwise stack a second Steam call.
   */
  steamFilling: boolean;
  /**
   * `gotyPickConfirm` is in flight — the same P3e-03 busy flag as `steamFilling`,
   * and the same double-tap guard, because CONFIRM is a plain `div` too and a
   * second tap would stack a second mint-and-add batch.
   *
   * It lives here rather than on `UiSlice` with the picker's other state because
   * it belongs to the async work, and because UI state is deliberately outside
   * `mutate`'s rollback snapshot.
   */
  gotyPickBusy: boolean;
  /**
   * Where the member's full Steam library check stands (migration 3070), or null
   * when we have not been told. The auto-fill dates play from achievements, one
   * Steam call per game, so the first pass over a 400-game library takes about a
   * minute and a half — run by the SERVER, and finished whether or not this tab
   * is still open. While `waiting` is true the button says how far along it is
   * instead of offering a list it knows is a sample.
   */
  steamBackfill: SteamBackfill | null;
  /**
   * The member pressed auto-fill and was told to wait: which year they asked
   * for, and when. It is what turns "the check finished" into "so open the
   * picker now" — but only on the screen they asked from, and only for
   * `FILL_WANTED_TTL_MS`; an hour-old press is not an instruction any more.
   *
   * IN MEMORY ONLY, deliberately. A reload closes sheets (store/resume.ts says
   * why), so a tab the phone evicted comes back to a button that tells the
   * truth — CHECKING… or ready — and one tap opens the complete list.
   */
  gotyFillWanted: { year: number; at: number } | null;

  myPots: () => GameId[];
  myTiers: () => Record<string, Tier>;
  myTierOrder: () => GameId[];
  mySkips: () => GameId[];
  myIntSkips: () => GameId[];

  /**
   * Make sure `crewIntOrder[userKey]` is populated. Fire-and-forget: nothing
   * awaits it, and the board re-renders when the answer lands.
   *
   * Called from `tfToggle` whenever the rail lands on exactly one member, which
   * is the only question this answers — so a member who never single-filters
   * costs no round trips, and one who does costs one per crewmate per session.
   */
  ensureCrewIntOrder: (userKey: string) => void;

  addPot: (id: GameId) => Promise<void>;
  placeTier: (id: GameId, t: Tier) => Promise<void>;
  placeTierAt: (id: GameId, t: Tier, idx: number) => Promise<void>;
  unplaceTier: (id: GameId) => Promise<void>;
  skipTodoId: (id: GameId) => Promise<void>;
  intSkip: (id: GameId) => Promise<void>;
  nudgeTier: (id: GameId, dir: 1 | -1) => Promise<void>;
  steamFill: () => Promise<void>;
  gotyPickConfirm: () => Promise<void>;
  /**
   * Re-read the library check's progress, and act on it: stop polling once there
   * is nothing to wait for, and — if the member asked and is still looking at
   * the GOTY screen — run the fill they asked for. Called by the poller, by the
   * focus handler when a backgrounded tab comes back, and after sign-in.
   */
  refreshSteamBackfill: () => Promise<void>;
  /** Stop the poller and forget the ask. Sign-out and teardown. */
  clearSteamBackfill: () => void;
}

/** How often the progress line is re-read while a member is waiting on it. A
 *  plain row read that asks Steam nothing, so this is about the UI feeling
 *  alive, not about cost — and it only runs while the tab is visible. */
export const BACKFILL_POLL_MS = 4000;
/** How long a press stays an instruction to open the picker. */
export const FILL_WANTED_TTL_MS = 30 * 60_000;

export const createBoardsSlice: StateCreator<QdStore, [], [], BoardsSlice> = (set, get) => {
  // ONE poller per store, held in the closure like steamFacts' retry timers: a
  // second press while waiting must not start a second one, and a module-level
  // handle would leak between the stores the tests build.
  let pollT: ReturnType<typeof setInterval> | null = null;
  const stopPoll = (): void => {
    if (pollT !== null) clearInterval(pollT);
    pollT = null;
  };
  const startPoll = (): void => {
    if (pollT !== null) return;
    pollT = setInterval(() => {
      // A hidden tab's timers are throttled to uselessness anyway, and the
      // focus handler re-reads the moment it comes back.
      if (typeof document !== 'undefined' && document.hidden) return;
      void get().refreshSteamBackfill();
    }, BACKFILL_POLL_MS);
  };

  return {
  gotyYear: GOTY_YEAR,
  gotyYears: [],
  gotyYearMenu: false,
  setGotyYear: async (y) => {
    const s = get();
    const m = s.me();
    if (!Number.isInteger(y) || y < 1990 || y > GOTY_YEAR) return;
    // Blank mine first: the old year's rows must not draw under the new
    // year's title while the read is in the air.
    set((st) => ({
      gotyYear: y,
      gotyYearMenu: false,
      gotyYears: st.gotyYears.includes(y) ? st.gotyYears : [...st.gotyYears, y].sort((a, b) => b - a),
      ...(m
        ? {
            potentials: { ...st.potentials, [m.k]: [] },
            tiers: { ...st.tiers, [m.k]: {} },
            tierOrder: { ...st.tierOrder, [m.k]: [] },
            skips: { ...st.skips, [m.k]: [] },
          }
        : {}),
    }));
    await get().refreshBoards();
  },
  toggleGotyYearMenu: (open) => set((st) => ({ gotyYearMenu: open ?? !st.gotyYearMenu })),
  potentials: {},
  tiers: {},
  tierOrder: {},
  skips: {},
  intOrder: {},
  intSkips: {},
  crewIntOrder: {},
  steamFilling: false,
  gotyPickBusy: false,
  steamBackfill: null,
  gotyFillWanted: null,

  myPots: () => get().potentials[get().session] ?? [],
  myTiers: () => get().tiers[get().session] ?? {},
  myTierOrder: () => get().tierOrder[get().session] ?? [],
  mySkips: () => get().skips[get().session] ?? [],
  myIntSkips: () => get().intSkips[get().session] ?? [],

  ensureCrewIntOrder: (userKey) => {
    if (!userKey) return;
    const s = get();
    // Mine is already in hand and is fresher than any answer a round trip could
    // give — `intOrder` carries the drag that has not been confirmed yet.
    if (userKey === s.session) return;
    // PRESENCE, not truthiness: `[]` is a real ordering. See the field's own
    // doc-comment for why a truthy guard would re-ask forever.
    if (userKey in s.crewIntOrder) return;
    if (crewOrderInFlight.has(userKey)) return;
    crewOrderInFlight.add(userKey);
    void (async () => {
      try {
        const ord = await s.repo.fetchCrewInterestOrder(userKey);
        set((st) => ({ crewIntOrder: { ...st.crewIntOrder, [userKey]: ord } }));
      } catch (e) {
        // NOTHING CACHED — the same call `ensureCrewLinks` makes next door and
        // for a sharper reason: `[]` here is a legitimate answer, so caching a
        // failure as one would leave the board silently sorted by the group
        // average while presenting as a member's own ranking. Unasked, it
        // retries the next time the rail lands on them.
        //
        // BUT NOT SILENTLY. A degraded read here looks like nothing at all: the
        // rows still render, just in the old order, which is precisely how the
        // unbound-`rpc` defect (`SupabaseRepository.binding.test.ts`) survived a
        // deploy on three sibling reads. It has to be diagnosable from the
        // console at least.
        console.error('[qd] crew interest order read failed — the tiers keep the average sort', e);
      } finally {
        crewOrderInFlight.delete(userKey);
      }
    })();
  },

  addPot: async (id) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    if (s.myPots().includes(id)) {
      s.toast('ALREADY IN YOUR POTENTIALS');
      return;
    }
    const potentials = [...s.myPots(), id];
    const g = s.gById(id);
    await mutate(storeApi(get, set), {
      key: 'boards:' + m.k,
      optimistic: (st) => ({ potentials: { ...st.potentials, [m.k]: potentials }, gq: '' }),
      commit: () => s.repo.saveBoards({ potentials }),
      toast: (g ? g.n.toUpperCase() : 'GAME') + ' → GOTY POTENTIALS',
    });
  },

  placeTier: async (id, t) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    const prev = s.myTiers()[String(id)];
    const tiers = { ...s.myTiers(), [String(id)]: t };
    const skips = s.mySkips().filter((x) => x !== id);
    const tierOrder = tierOrderAfterPlace(s.myTierOrder(), id);
    const g = s.gById(id);
    await mutate(storeApi(get, set), {
      key: 'boards:' + m.k,
      optimistic: (st) => ({
        tiers: { ...st.tiers, [m.k]: tiers },
        skips: { ...st.skips, [m.k]: skips },
        tierOrder: { ...st.tierOrder, [m.k]: tierOrder },
      }),
      commit: () => s.repo.saveBoards({ tiers, skips, tierOrder }),
      ...(prev === t ? {} : { toast: (g ? g.n.toUpperCase() : 'GAME') + ' → ' + TIER_NAMES[t] }),
    });
  },

  placeTierAt: async (id, t, idx) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    const prev = s.myTiers()[String(id)];
    const rows = tierRows(s.myPots(), s.myTiers(), s.myTierOrder());
    const tierOrder = tierOrderAfterDrop(rows, id, t, idx);
    const tiers = { ...s.myTiers(), [String(id)]: t };
    const skips = s.mySkips().filter((x) => x !== id);
    const g = s.gById(id);
    await mutate(storeApi(get, set), {
      key: 'boards:' + m.k,
      optimistic: (st) => ({
        tiers: { ...st.tiers, [m.k]: tiers },
        skips: { ...st.skips, [m.k]: skips },
        tierOrder: { ...st.tierOrder, [m.k]: tierOrder },
      }),
      commit: () => s.repo.saveBoards({ tiers, skips, tierOrder }),
      ...(prev === t ? {} : { toast: (g ? g.n.toUpperCase() : 'GAME') + ' → ' + TIER_NAMES[t] }),
    });
  },

  unplaceTier: async (id) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    const cur = s.myTiers();
    if (cur[String(id)] === undefined) return;
    const tiers = { ...cur };
    delete tiers[String(id)];
    const skips = skipsAfterSkip(s.mySkips(), id);
    const tierOrder = s.myTierOrder().filter((x) => x !== id);
    const g = s.gById(id);
    await mutate(storeApi(get, set), {
      key: 'boards:' + m.k,
      optimistic: (st) => ({
        tiers: { ...st.tiers, [m.k]: tiers },
        skips: { ...st.skips, [m.k]: skips },
        tierOrder: { ...st.tierOrder, [m.k]: tierOrder },
      }),
      commit: () => s.repo.saveBoards({ tiers, skips, tierOrder }),
      toast: (g ? g.n.toUpperCase() : 'GAME') + ' → BACK IN YOUR POTENTIALS',
    });
  },

  skipTodoId: async (id) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    const skips = skipsAfterSkip(s.mySkips(), id);
    await mutate(storeApi(get, set), {
      key: 'boards:' + m.k,
      optimistic: (st) => ({ skips: { ...st.skips, [m.k]: skips } }),
      commit: () => s.repo.saveBoards({ skips }),
      toast: 'SKIPPED — BACK OF THE QUEUE',
    });
  },

  /**
   * `SKIP FOR NOW ↻` on the MY INTERESTS TO-DO panel — `skipTodoId`'s twin, and
   * the same one-line body for the same reason: `skipsAfterSkip` is where "back
   * of the queue" is defined, and both boards mean the same thing by it.
   *
   * It writes the skip ONLY. Deferring a call is not making one, so no vote
   * moves and `intOrder` is untouched — which is also why this cannot ride on
   * `voteSet`'s key: a skip and a call on the same game are different writes
   * with different echoes. The `LAST ONE` guard is not here but in the two
   * screens, over the VM's `iSkipBlocked`, because "last" means last VISIBLE
   * and the filter decision lives in the view model (`skipTodoId` guards the
   * same way, `gTodoN`).
   */
  intSkip: async (id) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    const intSkips = skipsAfterSkip(s.myIntSkips(), id);
    await mutate(storeApi(get, set), {
      key: 'boards:' + m.k,
      optimistic: (st) => ({ intSkips: { ...st.intSkips, [m.k]: intSkips } }),
      commit: () => s.repo.saveBoards({ intSkips }),
      toast: 'SKIPPED — BACK OF THE QUEUE',
    });
  },

  nudgeTier: async (id, dir) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    const t = s.myTiers()[String(id)];
    if (t === undefined) return;
    const rows = tierRows(s.myPots(), s.myTiers(), s.myTierOrder());
    const tierOrder = tierOrderAfterNudge(rows, id, t, dir);
    if (!tierOrder) {
      s.toast(dir < 0 ? 'ALREADY FIRST' : 'ALREADY LAST');
      return;
    }
    await mutate(storeApi(get, set), {
      key: 'boards:' + m.k,
      optimistic: (st) => ({ tierOrder: { ...st.tierOrder, [m.k]: tierOrder } }),
      commit: () => s.repo.saveBoards({ tierOrder }),
      toast: dir < 0 ? 'MOVED EARLIER' : 'MOVED LATER',
    });
  },

  /**
   * `⟳ AUTO-FILL FROM STEAM` (INVENTORY §4.4) — the year's most-played games
   * from the member's own library, straight into their GOTY potentials.
   *
   * Reworked in G-01/G-02. The version this replaces was unable to do its job for
   * two independent reasons, and reported neither:
   *
   *   1. `op=goty` read `steam_owned_apps`, a table only the nightly sweep and
   *      CHECK NOW ever write. A linked-but-never-swept account — which is what
   *      every account is until one of those runs — had no rows, so the call
   *      succeeded with `[]`. An empty SUCCESS never reached the `catch`, so the
   *      demo-appid fallback did not fire either. The function now reads Steam
   *      live, so this no longer depends on a background job having run.
   *   2. Anything Steam returned was then dropped unless the crew's curated
   *      catalog already had it (`&& s.gById(id)`), because `add_goty_potential`
   *      rejects an unknown appid with `UNKNOWN_GAME`. A real library barely
   *      intersects a ~46-game catalog, so the filter ate nearly everything.
   *      Unknown appids are now MINTED first, through the same `ensure_game`
   *      path a live-search add uses.
   *
   * Both failures ended at `NOTHING NEW — YOUR 2026 HISTORY IS ALREADY IN`,
   * which is why the button read as dead rather than broken. Every distinguish-
   * able outcome now gets its own line.
   */
  steamFill: async () => {
    const s = get();
    const m = s.me();
    if (!m) return;
    // Before the gate, like `ptDoCheck`: if this is already running the gate has
    // by definition passed, so re-toasting it would be noise.
    if (s.steamFilling) return;
    const askedYear = get().gotyYear;
    const pastAsked = askedYear < GOTY_YEAR;
    const link = s.steamLink;
    const canAskSteam = !!(link && link.linked && link.profilePublic);

    // A PAST YEAR WITH NO STEAM TO ASK (AA §G): the picker offers that year's
    // catalog releases — every row `CAME OUT THIS YEAR — WE ARE GUESSING`, none
    // pre-ticked. It needs no link, which is why this sits above the gate.
    //
    // With Steam linked a past year is no longer only a guess: achievement
    // unlocks carry their own dates and a past year never changes, so the
    // server can say what was actually played. That path runs below, and these
    // same release guesses are appended to it, still unticked.
    if (pastAsked && !canAskSteam) {
      const offer = releaseGuesses(get, askedYear, new Set(s.myPots().map(Number)));
      if (!offer.length) {
        s.toast('NOTHING IN THE CATALOG CAME OUT IN ' + askedYear);
        return;
      }
      s.openGotyPick({ candidates: offer, library: [], counts: null, ticked: [] });
      return;
    }

    if (!canAskSteam || !link) {
      s.toast(steamFillGateToast(), true);
      return;
    }
    set({ steamFilling: true });
    // Every `return` below is inside the try, so the flag is cleared on all of
    // them as well as on a throw.
    try {
      // One button, not two. The member should not have to know that CHECK NOW
      // feeds the ledger this reads, nor press the pair in the right order.
      await refreshBeforeFill(get, set);
      // Re-read AFTER the await: the year row is live while the check runs.
      const year = get().gotyYear;
      const past = year < GOTY_YEAR;

      let res;
      try {
        res = await s.repo.steamFill(year);
      } catch (e) {
        // A PAST year loses nothing it had before: until 3070 it never asked
        // Steam at all, so when Steam cannot be asked — no key on this
        // deployment, an upstream blip — it falls back to exactly the
        // release-date guesses it always offered. Only a year with nothing to
        // fall back to reports the failure.
        if (past) {
          const guesses = releaseGuesses(get, year, new Set(s.myPots().map(Number)));
          if (guesses.length) {
            s.openGotyPick({ candidates: guesses, library: [], counts: null, ticked: [] });
            return;
          }
        }
        s.toast(steamFillErrorToast((e as { code?: unknown } | null)?.code), true);
        return;
      }

      if (!res.profilePublic) {
        // Steam answered, and the answer was "GAME DETAILS are private". Record
        // it so the sync chip and this button's own gate agree on the next
        // render. Re-read the link rather than reusing the one the gate saw:
        // the check above may have just advanced `lastSyncAt`, and spreading
        // the stale copy would throw that away.
        const cur = get().steamLink ?? link;
        set({ steamLink: { ...cur, profilePublic: false } });
        s.toast('STEAM GAME DETAILS ARE PRIVATE — SET THEM PUBLIC TO AUTO-FILL', true);
        return;
      }
      // Steam returned a library it will not DATE (G-07). The primary rule is
      // not merely empty here, it is unsatisfiable — `rtime_last_played` is
      // disclosed only to the account that owns the Web API key, so for every
      // member but one it never arrives. Saying "NO 2026 STEAM PLAYTIME TO PULL
      // IN" to somebody with 2,600 hours on record is the exact class of lie
      // G-04 was written to remove, so instead the fallback's own findings go to
      // the picker and the member says which of them are real.
      // THE FULL LIBRARY CHECK IS STILL RUNNING (migration 3070). The server is
      // asking Steam about every game they own, one call each — about a minute
      // and a half for 400 — and it finishes whether or not this tab stays open.
      // What it has so far is a sample, and offering a sample as the list is the
      // thing this whole change exists to stop. So: no picker. Remember that
      // they asked, show how far along it is, and open it when it is done.
      if (res.backfill?.waiting) {
        set({ steamBackfill: res.backfill, gotyFillWanted: { year, at: Date.now() } });
        startPoll();
        return;
      }
      // Anything else is an answer — including `failed` and `blocked_private`,
      // which deliver what they have rather than leave anyone waiting.
      set({ steamBackfill: res.backfill ?? null, gotyFillWanted: null });
      stopPoll();

      const have = new Set(s.myPots().map(Number));
      if (past) {
        // Dated rows lead; the release-date guesses follow, unticked as ever. A
        // function older than this contract may still answer a past year with
        // `dated: true` and a `games` list meant to be committed unseen — those
        // go through the picker too. "Last played in 2025" is evidence, not an
        // instruction to write on a board.
        const dated: GotyCandidate[] = res.dated
          ? res.games.map((g) => ({
            appid: g.appid, name: g.name, playtimeMin: g.playtimeMin,
            signal: 'played', signals: ['played'], evidenceAt: g.lastPlayedAt, confident: true,
          }))
          : res.candidates;
        const offered = dated.filter((c) => !have.has(c.appid));
        const seen = new Set([...have, ...offered.map((c) => c.appid)]);
        const offer = [...offered, ...releaseGuesses(get, year, seen)];
        // Opened even when `offer` is empty: the picker says so itself and the
        // library search below it is then the way in.
        s.openGotyPick({
          candidates: offer,
          library: res.library.filter((g) => !have.has(g.appid)),
          counts: res.counts,
          ticked: offer.filter((c) => c.confident).map((c) => c.appid),
        });
        return;
      }

      if (!res.dated) {
        const offer = res.candidates.filter((c) => !have.has(c.appid));
        s.openGotyPick({
          candidates: offer,
          library: res.library.filter((g) => !have.has(g.appid)),
          counts: res.counts,
          // Pre-tick the OBSERVED signals only. `released` is an inference —
          // "shipped that year with no trace of Early Access" — and the trace it
          // reads is absent for roughly a third of formerly-Early-Access games,
          // so ticking it by default would quietly write guesses onto a board.
          ticked: offer.filter((c) => c.confident).map((c) => c.appid),
        });
        return;
      }

      if (!res.games.length) {
        // Reachable only when Steam DID date the library, so this is true.
        s.toast('NO ' + year + ' STEAM PLAYTIME TO PULL IN');
        return;
      }

      const out = await commitGotyPicks(
        get,
        set,
        m.k,
        res.games,
        (n) => 'ADDED ' + n + ' FROM YOUR ' + year + ' STEAM HISTORY',
      );
      if (out === 'none') s.toast('NOTHING NEW — YOUR ' + year + ' HISTORY IS ALREADY IN');
      else if (out === 'failed') s.toast("✕ COULDN'T ADD THOSE — TRY AGAIN", true);
    } finally {
      set({ steamFilling: false });
    }
  },

  /**
   * Write the picker's ticked rows to the board (G-07).
   *
   * The order is the order the member SAW: candidates in the server's ranked
   * order first, then anything they found through the library search, in the
   * order that list showed them. Building from the ticked-set alone would order
   * the board by whatever sequence they happened to tap in.
   */
  gotyPickConfirm: async () => {
    const s = get();
    const m = s.me();
    if (!m) return;
    if (s.gotyPickBusy) return;

    const on = new Set(s.gotyPickOn);
    if (on.size === 0) {
      s.toast('TICK AT LEAST ONE GAME FIRST', true);
      return;
    }
    const picks: GotyPick[] = [];
    const seen = new Set<number>();
    for (const c of s.gotyPickCands) {
      if (!on.has(c.appid) || seen.has(c.appid)) continue;
      seen.add(c.appid);
      picks.push({ appid: c.appid, name: c.name });
    }
    for (const g of s.gotyPickLib) {
      if (!on.has(g.appid) || seen.has(g.appid)) continue;
      seen.add(g.appid);
      picks.push({ appid: g.appid, name: g.name });
    }

    set({ gotyPickBusy: true });
    try {
      const out = await commitGotyPicks(
        get,
        set,
        m.k,
        picks,
        (n) => 'ADDED ' + n + ' TO YOUR ' + get().gotyYear + ' POTENTIALS',
      );
      if (out === 'failed') {
        s.toast("✕ COULDN'T ADD THOSE — TRY AGAIN", true);
        return;
      }
      if (out === 'none') {
        s.toast('NOTHING NEW — THOSE ARE ALREADY IN');
        return;
      }
      // Closed on the OPTIMISTIC patch, not on the commit: `mutate` toasts before
      // the round trip resolves and rolls the board back itself if it fails, the
      // same contract `ptDoTrack` closes its sheet under.
      s.closeGotyPick();
    } finally {
      set({ gotyPickBusy: false });
    }
  },

  refreshSteamBackfill: async () => {
    const s = get();
    if (!s.session) {
      stopPoll();
      return;
    }
    let b: SteamBackfill | null;
    try {
      b = await s.repo.fetchSteamBackfill();
    } catch {
      return; // a progress line that cannot be read is simply not updated
    }
    set({ steamBackfill: b });
    if (b?.waiting) {
      // Also how a RELOADED tab picks the progress line back up: sign-in reads
      // this once, finds a pass under way, and the poll resumes.
      startPoll();
      return;
    }
    stopPoll();

    const wanted = get().gotyFillWanted;
    if (wanted === null) return;
    set({ gotyFillWanted: null });
    // `b === null` is "could not tell", NOT "finished". Acting on it would ask
    // the server again, be told to wait again, and loop every four seconds.
    if (b === null || Date.now() - wanted.at > FILL_WANTED_TTL_MS) return;

    const here = get();
    const stillThere = here.screen === 'goty' && here.gotyYear === wanted.year &&
      !here.steamFilling && !here.gotyPickOpen;
    if (stillThere) {
      // The fill they asked for, now that it can be complete. If they left the
      // tab, this runs when they come back — the focus handler calls us.
      await here.steamFill();
    } else {
      // They moved on. Opening a sheet over whatever they are doing now would
      // be an ambush; say it is ready and leave the button to them.
      here.toast('YOUR STEAM LIBRARY CHECK IS DONE — AUTO-FILL IS READY');
    }
  },

  clearSteamBackfill: () => {
    stopPoll();
    set({ steamBackfill: null, gotyFillWanted: null });
  },
  };
};
